import { ColumnApi, GridApi } from '@ag-grid-community/core';
import { DependencyList, useCallback, useMemo } from 'react';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER } from '../../pages/Companies/Company.constants';

export type TableQueryResult = {
  sort: {
    sortField: string;
    sortOrderAsc: boolean;
  };
  page: number;
  onSortChanged: <T extends { columnApi: ColumnApi; api?: GridApi }>(event: T) => void;
  onPageChanged: (page: number) => void;
};

export default (
  extraDependencies: DependencyList = [],
  defaultSortField = DEFAULT_SORT_FIELD,
  defaultSortOrder = DEFAULT_SORT_ORDER
): TableQueryResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = useMemo(() => {
    const sortOrder = searchParams.get('sortOrder') ?? defaultSortOrder;
    const sortField = searchParams.get('sortField') ?? defaultSortField;

    return {
      sortField,
      sortOrderAsc: sortOrder !== 'desc',
    };
  }, [searchParams, defaultSortOrder, defaultSortField]);

  const page = useMemo(() => {
    const page = Number(searchParams.get('page') ?? 1);

    return page > 0 ? page : 1;
  }, [searchParams]);

  const onSortChanged = useCallback(
    (event) => {
      if (event.api?.getDisplayedRowCount() > 0) {
        event.api.ensureIndexVisible(0, 'top');
      }

      const column = event.columnApi.getColumnState().find((c) => c.sort);
      const params = createSearchParams(searchParams);
      if (!column) {
        params.set('sortField', defaultSortField);
        params.set('sortOrder', defaultSortOrder);
        event.columnApi.applyColumnState({ state: [{ colId: defaultSortField, sort: defaultSortOrder }] });
      } else {
        params.set('sortField', column.colId);
        params.set('sortOrder', column.sort || defaultSortOrder);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, ...extraDependencies]
  );

  const onPageChanged = useCallback(
    (page) => {
      const params = createSearchParams(searchParams);
      params.set('page', page);
      setSearchParams(params, { replace: true });
    },
    [searchParams, ...extraDependencies]
  );

  return {
    page,
    onSortChanged,
    sort,
    onPageChanged,
  };
};
